import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, shareReplay } from 'rxjs/operators';
import { Observable, forkJoin } from 'rxjs';
import { Environment } from '../environment/environment';

@Injectable({
  providedIn: 'root'
})
export class ExportService {

  constructor(
    private http: HttpClient
  ) { }

  private fontCache: Record<string, Observable<string>> = {};


  private getElementTag(tag: keyof HTMLElementTagNameMap): string {
    const html: string[] = [];
    const elements = document.getElementsByTagName(tag);
    for (let index = 0; index < elements.length; index++) {
      html.push(elements[index].outerHTML);
    }
    return html.join('\r\n');
  }

  public extractExportHTML(targetElementId: string): Observable<{ content: string, styles: string, links: string }> {
    const content = document.getElementById(targetElementId)?.innerHTML;

    let styles = this.getElementTag('style');
    let links = this.getElementTag('link');

    // External style bundle is now included via link.
    // Get styles.css file and insert as inline style.
    return this.http.get('/styles.css', { responseType: 'text' }).pipe(map(v => {
      styles += `<style>${v}</style>`;
      links.replace('<link rel=\"stylesheet\" href=\"styles.css\">', '');

      return {
        content,
        styles,
        links
      };
    }));
  }


  public getOnlyStyles(): Observable<{styles: string, links: string}>{
    let styles = this.getElementTag('style');
    let links = this.getElementTag('link');

    // External style bundle is now included via link.
    // Get styles.css file and insert as inline style.
    return this.http.get('/styles.css', { responseType: 'text' }).pipe(map(v => {
      styles += `<style>${v}</style>`;
      links.replace('<link rel=\"stylesheet\" href=\"styles.css\">', '');

      return {
        styles,
        links
      };
    }));
  }

  public encodeHTML(s: string): string {
    // Converts string to UTF-8, then converts to base64.
    const utf8 = unescape(encodeURIComponent(s));

    return window.btoa(utf8);
  }

  public setDownloadTarget(targetId: string, filename: string, filecontent: string, datatype: string) {
    const anchor = document.getElementById(targetId);

    if (anchor) {
      anchor.setAttribute('download', filename);
      anchor.setAttribute('href', `data:${datatype};charset=UTF-8;base64,${this.encodeHTML(filecontent)}`);
    }
  }

  public downloadFile(filename: string, filecontent: string, datatype: string) {
    const blob = new Blob([filecontent], { type: datatype });
    this.downloadBlob(filename, blob);
  }

  // Dispara o download de um Blob já montado (ex.: .xlsx gerado via JSZip).
  public downloadBlob(filename: string, blob: Blob): void {
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.setAttribute('download', filename);
    anchor.setAttribute('href', url);

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  

  public setDownloadMultiTarget(selector: string, filename: string, filecontent: string, datatype: string) {
    const selected = document.querySelectorAll(selector);

    selected.forEach(anchor => {
      anchor.setAttribute('download', filename);
      anchor.setAttribute('href', `data:${datatype};charset=UTF-8;base64,${this.encodeHTML(filecontent)}`);
    });
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    // Convert text to base64
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;

    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
  }

  loadFontAsBase64$(url: string): Observable<string> {
    // Load font and transform it to base64
    if (!this.fontCache[url]) {
      this.fontCache[url] = this.http.get(url, { responseType: 'arraybuffer' }).pipe(
        map(buffer => this.arrayBufferToBase64(buffer)),
        shareReplay(1)
      );
    }
    return this.fontCache[url];
  }

  loadJemsFonts(): Observable<{ jems3s: string; jems3r: string }> {
    // Load both fonts and return as a single object
    const baseUrl = window.location.origin;

    return forkJoin([
      this.loadFontAsBase64$(`${baseUrl}/assets/fonts/jems3s.woff`),
      this.loadFontAsBase64$(`${baseUrl}/assets/fonts/jems3r.woff`)
    ]).pipe(
      map(([jems3s, jems3r]) => ({ jems3s, jems3r })),
      shareReplay(1)
    );
  }

  transformHtmlToPdf(html: string, pageSize?: { width: string, height?: string }): Observable<Blob>{
    // pageSize (optional): custom PDF page dimensions (e.g. '1200px'). Absent → backend uses A4.
    // width sem height → o backend gera uma ÚNICA página longa (altura = conteúdo).
    return this.http.post<Blob>(Environment.urls.API + '/core/htmlToPdf/', {html, ...pageSize}, { responseType: 'arraybuffer' as 'json'} // Tell Angular to treat it as raw binary data
    ).pipe(
      map((buffer: any) => {
        // Manually wrap the buffer in a Blob
        return new Blob([buffer], { type: 'application/pdf' });
      })
    );
  }
}
